import { useTranslation } from 'react-i18next';
import { PhotoProofViewer } from '@/shared/ui';

/**
 * The guardian review presentation of a submitted photo proof (parent-preview mockup 11
 * `11-chore-review.html`). An uppercase "ФОТО-ПІДТВЕРДЖЕННЯ" section header, then the photo filling a
 * fixed 180px frame with an attribution tag overlaid bottom-start ("Додала {name} о 14:18" — the
 * submitter from the roster, the time only when it's known). The photo opens the shared lightbox on
 * tap (the {@link PhotoProofViewer} button fills the frame). This is read-only: a guardian never
 * captures the proof, so there is no capture affordance here — the kid/assignee path keeps that.
 *
 * Page-private to the chore-detail slice (the guardian review screen); not exported from the barrel.
 */
export function GuardianProofPhoto({
  photoKey,
  choreTitle,
  submitterName,
  submittedTimeLabel,
}: {
  photoKey: string;
  /** The chore title, woven into the viewer's alt text. User content — never translated. */
  choreTitle: string;
  /** The assignee's display name (roster). Omitted while the roster is still loading. */
  submitterName: string | null;
  /** "14:18" — the proof's submission time in the household zone, or null when unknown. */
  submittedTimeLabel: string | null;
}): React.ReactElement {
  const { t } = useTranslation('family');
  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-text-muted">
        {t('choreDetail.photoTitle')}
      </h2>
      <div className="relative h-[180px] overflow-hidden rounded-md border border-border">
        <PhotoProofViewer
          photoKey={photoKey}
          choreTitle={choreTitle}
          className="block size-full overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        />
        {submitterName != null && (
          <span className="pointer-events-none absolute bottom-2.5 start-2.5 inline-flex items-center gap-1.5 rounded-pill border border-border bg-surface/90 px-2.5 py-1 text-xs font-semibold text-text backdrop-blur-sm">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
              <rect
                x="3"
                y="7"
                width="18"
                height="13"
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle cx="12" cy="13.5" r="3.3" stroke="currentColor" strokeWidth="2" />
              <path
                d="M8.5 7l1.2-2h4.6L15.5 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
            {submittedTimeLabel != null
              ? t('choreDetail.photoSubmittedByAt', {
                  name: submitterName,
                  time: submittedTimeLabel,
                })
              : t('choreDetail.photoSubmittedBy', { name: submitterName })}
          </span>
        )}
      </div>
    </div>
  );
}
